import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { ExcursionAlert } from '@/modules/ezparcel/components/coldchain/ExcursionAlert';
import {
  AlertTriangle,
  History,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingDown,
  FileText } from
'lucide-react';

export const ExcursionManagement = () => {const { t } = useTranslation("ezparcel");
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredExcursions = mockExcursions.filter((exc) => {
    if (filterSeverity !== 'all' && exc.severity !== filterSeverity) return false;
    if (filterStatus !== 'all' && exc.status !== filterStatus) return false;
    return true;
  });

  const activeCount = mockExcursions.filter((e) => e.status === 'Active').length;
  const acknowledgedCount = mockExcursions.filter((e) => e.status === 'Acknowledged').length;
  const criticalCount = mockExcursions.filter((e) => e.severity === 'Critical').length;

  const resolutionColors = {
    'RESOLVED_OK': 'bg-emerald-100 text-emerald-700',
    'RESOLVED_ADJUSTED': 'bg-blue-100 text-blue-700',
    'QUARANTINED': 'bg-amber-100 text-amber-700',
    'REJECTED': 'bg-red-100 text-red-700',
    'REROUTED': 'bg-violet-100 text-violet-700',
    'SENSOR_REPLACED': 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="space-y-6" data-testid="coldchain-excursions">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-600" />{t("excursionManagement", "Excursion Management")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("temperatureExcursionsRequiring", "Temperature excursions requiring attention and resolution")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <FileText className="h-4 w-4" />{t("exportReport", "Export Report")}

          </Button>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-red-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-100">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{activeCount}</p>
              <p className="text-sm text-muted-foreground">{t("active", "Active")}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-amber-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-100">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{acknowledgedCount}</p>
              <p className="text-sm text-muted-foreground">{t("acknowledged", "Acknowledged")}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-red-300">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-100">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{criticalCount}</p>
              <p className="text-sm text-muted-foreground">{t("critical", "Critical")}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-emerald-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-emerald-100">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{mockExcursionHistory.length}</p>
              <p className="text-sm text-muted-foreground">{t("resolvedToday", "Resolved Today")}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t("filters", "Filters:")}</span>
            </div>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allSeverity", "All Severity")}</SelectItem>
                <SelectItem value="Critical">{t("critical", "Critical")}</SelectItem>
                <SelectItem value="Warning">{t("warning", "Warning")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="Active">{t("active", "Active")}</SelectItem>
                <SelectItem value="Acknowledged">{t("acknowledged", "Acknowledged")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Active Excursions */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />{t("currentExcursions", "Current Excursions (")}
          {filteredExcursions.length})
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {filteredExcursions.map((excursion) =>
          <ExcursionAlert
            key={excursion.id}
            excursion={excursion}
            onAcknowledge={(e) => console.log('Acknowledge:', e.id)}
            onResolve={(e) => console.log('Resolve:', e.id)}
            onReroute={(e) => console.log('Reroute:', e.id)} />

          )}
        </div>
      </div>

      {/* Excursion History */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-muted-foreground" />{t("recentResolutionHistory", "Recent Resolution History")}

          </CardTitle>
          <CardDescription>{t("excursionsResolvedIn", "Excursions resolved in the last 7 days")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="py-3 px-4 text-left font-medium">{t("date", "Date")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("sensor", "Sensor")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("type", "Type")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("deviation", "Deviation")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("duration", "Duration")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("resolution", "Resolution")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("resolvedBy", "Resolved By")}</th>
                </tr>
              </thead>
              <tbody>
                {mockExcursionHistory.map((item) =>
                <tr key={item.id} className="border-b border-border hover:bg-muted/30">
                    <td className="py-3 px-4 text-sm">{item.date}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="font-mono">{item.sensorId}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm">{item.type}</td>
                    <td className="py-3 px-4">
                      {item.maxDeviation !== null ?
                    <span className={`font-mono ${item.maxDeviation > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                          {item.maxDeviation > 0 ? '+' : ''}{item.maxDeviation}{t("c", "\xB0C")}
                    </span> :

                    <span className="text-muted-foreground">{t("na", "N/A")}</span>
                    }
                    </td>
                    <td className="py-3 px-4 text-sm">{item.duration}{t("min", "min")}</td>
                    <td className="py-3 px-4">
                      <Badge className={resolutionColors[item.resolutionCode]}>
                        {resolutionCodes.find((r) => r.code === item.resolutionCode)?.label}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm">{item.resolvedBy}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>);

};